import React, { useCallback, useState } from 'react';

import { Delete } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from 'react-query';
import { getWalletFundClasses } from 'src/services/wallet';
import { debounce } from 'src/utils/general';

type QuotaRecord = {
  id: string;
  class: string;
  series: string | number;
  amount: number;
};

type QuotaInputRowProps = {
  fundId: string;
  quota: QuotaRecord;
  index: number;
  fundCnpj?: string; // CNPJ do fundo para buscar classes
  onUpdate: (
    fundId: string,
    quotaId: string,
    field: keyof QuotaRecord,
    value: string | number,
  ) => void;
  onRemove: (fundId: string, quotaId: string) => void;
  hasError?: boolean;
};

export function QuotaInputRow({
  fundId,
  quota,
  index,
  fundCnpj,
  onUpdate,
  onRemove,
  hasError = false,
}: QuotaInputRowProps) {
  const [searchFundQuotaClass, setSearchFundQuotaClass] = useState('');
  const [inputFocusedFundQuotaClass, setInputFocusedFundQuotaClass] =
    useState(false);

  const debouncedSetSearchFundQuotaClass = useCallback(
    debounce((value: string) => {
      setSearchFundQuotaClass(value);
    }, 500),
    [],
  );

  const { data: classesFromApi, isLoading: isLoadingClasses } = useQuery({
    queryKey: ['classes', fundCnpj, searchFundQuotaClass, fundId],
    queryFn: () => getWalletFundClasses(fundCnpj ?? ''),
    enabled:
      inputFocusedFundQuotaClass &&
      searchFundQuotaClass !== undefined &&
      !!fundCnpj,
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true,
  });

  const handleClassChange = useCallback(
    (value: string) => {
      onUpdate(fundId, quota.id, 'class', value);
    },
    [onUpdate, fundId, quota.id],
  );

  const handleSeriesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      onUpdate(fundId, quota.id, 'series', value);
    },
    [onUpdate, fundId, quota.id],
  );

  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value) || 0;
      onUpdate(fundId, quota.id, 'amount', value);
    },
    [onUpdate, fundId, quota.id],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        '&:last-of-type hr': {
          display: 'none',
        },
      }}>
      <Box
        display="flex"
        gap={2}
        alignItems="flex-start"
        mb={1}
        flexDirection={{ xs: 'column', md: 'row' }}>
        <FormControl fullWidth variant="outlined">
          <Autocomplete
            freeSolo
            blurOnSelect
            popupIcon={null}
            loadingText="Carregando subclasses..."
            noOptionsText={<Box>Nenhuma classe encontrada</Box>}
            loading={isLoadingClasses}
            options={
              Array.isArray(classesFromApi)
                ? [...new Set(classesFromApi.map((c) => c.subclasse))]
                : []
            }
            renderOption={(props, option) => (
              <Box {...props} component="li" sx={{ display: 'flex', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {option}
                </Typography>
              </Box>
            )}
            getOptionLabel={(option) => option}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                handleClassChange(value);
              }
              debouncedSetSearchFundQuotaClass(value);
            }}
            isOptionEqualToValue={(option, value) => option === value}
            onChange={(_, value) => {
              const newValue = typeof value === 'string' ? value : value || '';
              handleClassChange(newValue);
            }}
            value={quota.class}
            renderInput={(params) => (
              <TextField
                {...params}
                value={quota.class}
                size="small"
                label="Classe de cota"
                onFocus={() => {
                  setInputFocusedFundQuotaClass(true);
                  if (!searchFundQuotaClass) {
                    setSearchFundQuotaClass('');
                  }
                }}
                onBlur={() => {
                  setInputFocusedFundQuotaClass(false);
                }}
                InputProps={{
                  ...params.InputProps,
                }}
                error={!quota.class}
                helperText={!quota.class ? 'Classe é obrigatória' : ''}
              />
            )}
          />
        </FormControl>
        <FormControl variant="outlined" size="small">
          <TextField
            value={quota.series}
            label="Série"
            size="small"
            onChange={handleSeriesChange}
            placeholder="Ex: 1, 2, única, ..."
            error={!quota.series}
            helperText={!quota.series ? 'Campo obrigatório' : ''}
          />
        </FormControl>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl variant="outlined" size="small">
            <TextField
              value={quota.amount}
              size="small"
              label="Qtd de cotas"
              type="number"
              onChange={handleAmountChange}
              inputProps={{ min: 0, step: 1 }}
              placeholder="0"
              error={quota.amount <= 0}
              helperText={quota.amount <= 0 ? 'Mínimo de 1 cota' : ''}
            />
          </FormControl>
          <IconButton
            onClick={() => {
              onRemove(fundId, quota.id);
            }}
            size="small"
            color="error"
            title="Remover cota">
            <Delete />
          </IconButton>
        </Box>
      </Box>
      <Divider
        sx={{ width: '100%', mb: 1.5, mt: 0.5, borderStyle: 'dashed' }}
      />
    </Box>
  );
}
